import { useRef, useState } from 'react';
import { useAuthStore } from '../lib/auth';
import { API_BASE } from '../lib/apiBase';

export interface ApplianceData {
  type: string;
  make: string;
  model: string;
  serial_number: string;
  install_year: number | null;
  btu_rating: number | null;
  notes: string;
}

interface ScanResult {
  brand: string | null;
  model_number: string | null;
  serial_number: string | null;
  manufacture_year: number | null;
  capacity: string | null;
  btu_rating: number | null;
  appliance_type: string;
  confidence: 'high' | 'medium' | 'low';
  notes: string | null;
}

interface ApplianceScannerProps {
  homeId: string;
  applianceType?: string;
  onComplete: (data: ApplianceData) => void;
  onClose: () => void;
}

type State = 'idle' | 'analyzing' | 'confirming' | 'done';

const CONFIDENCE_STYLE: Record<ScanResult['confidence'], { bg: string; border: string; color: string; icon: string; label: string }> = {
  high:   { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d', icon: '✓', label: 'Label read clearly' },
  medium: { bg: '#fffbeb', border: '#fde68a', color: '#92400e', icon: '⚠', label: 'Please verify these details' },
  low:    { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', icon: '✗', label: 'Could not read clearly — please enter manually' },
};

const MAX_FILE_BYTES = 5 * 1024 * 1024;

function Spinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '32px 0' }}>
      <div style={{
        width: 56, height: 56,
        border: '4px solid #e9d5ff',
        borderTopColor: '#9333EA',
        borderRadius: '50%',
        animation: 'spin 0.75s linear infinite',
      }} />
      <p style={{ margin: 0, fontSize: 15, color: '#6b7280', fontWeight: 500 }}>
        Claude is reading your appliance label…
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function ApplianceScanner({ homeId: _homeId, applianceType = '', onComplete, onClose }: ApplianceScannerProps) {
  void _homeId;
  const accessToken = useAuthStore((s) => s.accessToken);
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<State>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [apiError, setApiError] = useState('');

  // Editable confirmation fields
  const [fields, setFields] = useState<ApplianceData>({
    type: applianceType,
    make: '',
    model: '',
    serial_number: '',
    install_year: null,
    btu_rating: null,
    notes: '',
  });

  function setField<K extends keyof ApplianceData>(key: K, value: ApplianceData[K]) {
    setFields(f => ({ ...f, [key]: value }));
  }

  async function processFile(file: File) {
    if (file.size > MAX_FILE_BYTES) {
      setApiError('Image must be 5 MB or smaller. Please choose a smaller photo.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setApiError('Only JPEG, PNG, and WEBP images are supported.');
      return;
    }

    setApiError('');
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setState('analyzing');

    const form = new FormData();
    form.append('image', file);
    form.append('appliance_type', applianceType || 'unknown');

    try {
      const res = await fetch(`${API_BASE}/scanner/appliance`, {
        method: 'POST',
        credentials: 'include',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        body: form,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: { message: string } };
        setApiError(body.error?.message ?? 'Scanner error. Please enter details manually.');
        setState('confirming');
        setScanResult(null);
        return;
      }

      const result = await res.json() as ScanResult;
      setScanResult(result);
      setFields({
        type: result.appliance_type !== 'other' ? result.appliance_type : (applianceType || ''),
        make: result.brand ?? '',
        model: result.model_number ?? '',
        serial_number: result.serial_number ?? '',
        install_year: result.manufacture_year ?? null,
        btu_rating: result.btu_rating ?? null,
        notes: result.notes ?? '',
      });
      setState('confirming');
    } catch {
      setApiError('Network error. You can still enter details manually below.');
      setScanResult(null);
      setState('confirming');
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void processFile(file);
    e.target.value = '';
  }

  function handleSave() {
    onComplete(fields);
    setState('done');
    setTimeout(() => onClose(), 1200);
  }

  function tryAgain() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setScanResult(null);
    setApiError('');
    setState('idle');
  }

  const inp = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #d1d5db',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box' as const,
    background: '#fff',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          maxHeight: '92dvh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: '#111827',
          borderRadius: '20px 20px 0 0',
          padding: '18px 20px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff' }}>
              Scan Appliance Label
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
              Point camera at label or data plate
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.12)', border: 'none',
              color: '#fff', fontSize: 18, width: 36, height: 36,
              borderRadius: '50%', cursor: 'pointer', lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 20px 32px', flex: 1 }}>

          {/* ── IDLE ── */}
          {state === 'idle' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {apiError && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 10, padding: '10px 14px',
                  fontSize: 13, color: '#dc2626',
                }}>
                  {apiError}
                </div>
              )}

              <p style={{ margin: '0 0 8px', fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
                Take a photo of the label on your appliance — usually on the back, side, or inside the door.
              </p>

              {/* Camera button */}
              <button
                onClick={() => cameraRef.current?.click()}
                style={{
                  width: '100%',
                  padding: '18px 16px',
                  background: '#9333EA',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 16,
                  fontSize: 17,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  minHeight: 64,
                }}
              >
                <span style={{ fontSize: 24 }}>📷</span>
                Scan with camera
              </button>

              {/* File picker button */}
              <button
                onClick={() => fileRef.current?.click()}
                style={{
                  width: '100%',
                  padding: '18px 16px',
                  background: '#fff',
                  color: '#9333EA',
                  border: '2px solid #9333EA',
                  borderRadius: 16,
                  fontSize: 17,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  minHeight: 64,
                  boxSizing: 'border-box',
                }}
              >
                <span style={{ fontSize: 24 }}>🖼️</span>
                Upload a photo
              </button>

              {/* Hidden inputs */}
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          )}

          {/* ── ANALYZING ── */}
          {state === 'analyzing' && (
            <div>
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Appliance label"
                  style={{
                    width: '100%', maxHeight: 220, objectFit: 'cover',
                    borderRadius: 12, marginBottom: 20,
                  }}
                />
              )}
              <Spinner />
            </div>
          )}

          {/* ── CONFIRMING ── */}
          {state === 'confirming' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Thumbnail */}
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Scanned label"
                  style={{
                    width: '100%', maxHeight: 160, objectFit: 'cover',
                    borderRadius: 12, flexShrink: 0,
                  }}
                />
              )}

              {/* API error fallback */}
              {apiError && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 10, padding: '10px 14px',
                  fontSize: 13, color: '#dc2626',
                }}>
                  {apiError} — Fill in what you can below.
                </div>
              )}

              {/* Confidence badge */}
              {scanResult && (() => {
                const cs = CONFIDENCE_STYLE[scanResult.confidence];
                return (
                  <div style={{
                    background: cs.bg, border: `1px solid ${cs.border}`,
                    borderRadius: 10, padding: '10px 14px',
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: cs.color, fontWeight: 600,
                  }}>
                    <span style={{ fontSize: 16 }}>{cs.icon}</span>
                    {cs.label}
                  </div>
                );
              })()}

              {/* Editable fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Brand / Make</label>
                  <input
                    style={inp}
                    value={fields.make}
                    onChange={e => setField('make', e.target.value)}
                    placeholder="e.g. Carrier"
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Model Number</label>
                  <input
                    style={inp}
                    value={fields.model}
                    onChange={e => setField('model', e.target.value)}
                    placeholder="e.g. 58MVC090"
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Serial Number</label>
                  <input
                    style={inp}
                    value={fields.serial_number}
                    onChange={e => setField('serial_number', e.target.value)}
                    placeholder="Optional"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Year Manufactured</label>
                    <input
                      style={inp}
                      type="number"
                      value={fields.install_year ?? ''}
                      onChange={e => setField('install_year', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="e.g. 2019"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>BTU Rating</label>
                    <input
                      style={inp}
                      type="number"
                      value={fields.btu_rating ?? ''}
                      onChange={e => setField('btu_rating', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="e.g. 36000"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Appliance Type</label>
                  <select
                    style={{ ...inp, cursor: 'pointer' }}
                    value={fields.type}
                    onChange={e => setField('type', e.target.value)}
                  >
                    <option value="">Select type…</option>
                    <option value="hvac">HVAC</option>
                    <option value="water_heater">Water Heater</option>
                    <option value="washer">Washer</option>
                    <option value="dryer">Dryer</option>
                    <option value="refrigerator">Refrigerator</option>
                    <option value="dishwasher">Dishwasher</option>
                    <option value="oven">Oven</option>
                    <option value="microwave">Microwave</option>
                    <option value="range">Range / Stove</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Notes</label>
                  <textarea
                    style={{ ...inp, resize: 'none' }}
                    rows={2}
                    value={fields.notes}
                    onChange={e => setField('notes', e.target.value)}
                    placeholder="Any other details from the label"
                  />
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                <button
                  onClick={handleSave}
                  style={{
                    width: '100%', padding: '14px',
                    background: '#9333EA', color: '#fff',
                    border: 'none', borderRadius: 14,
                    fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  ✓ Save appliance
                </button>
                <button
                  onClick={tryAgain}
                  style={{
                    width: '100%', padding: '12px',
                    background: 'transparent', color: '#6b7280',
                    border: '1.5px solid #d1d5db', borderRadius: 14,
                    fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* ── DONE ── */}
          {state === 'done' && (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '40px 0', gap: 16,
            }}>
              <div style={{
                width: 72, height: 72,
                background: '#dcfce7',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36,
              }}>
                ✅
              </div>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#15803d' }}>
                Appliance saved!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
